import { useEffect, useState } from 'react';
import type { ShopDTO, WbCardLimitsDTO } from './api';
import { api } from './api';

type ReadyLimits = WbCardLimitsDTO & { status: 'ready'; totalRemaining: number; fetchedAt: string };
export type ShopCardLimitsView =
  | { state: 'credentials' }
  | { state: 'loading' }
  | { state: 'refreshing'; limits: WbCardLimitsDTO }
  | { state: 'ready'; limits: ReadyLimits }
  | { state: 'error'; message: string };

type Outcome = Exclude<ShopCardLimitsView, { state: 'credentials' } | { state: 'loading' }>;
type ShopRequest = Pick<ShopDTO, 'id' | 'credentialVersion'>;

let activeRequests = 0;
type RequestWaiter = {
  current: () => boolean;
  signal?: AbortSignal;
  resolve: (acquired: boolean) => void;
  onAbort: () => void;
  active: boolean;
};
const waitingRequests: RequestWaiter[] = [];

function removeWaiter(waiter: RequestWaiter): void {
  const index = waitingRequests.indexOf(waiter);
  if (index >= 0) waitingRequests.splice(index, 1);
}

function cleanupWaiter(waiter: RequestWaiter): void {
  waiter.signal?.removeEventListener('abort', waiter.onAbort);
}

function acquireRequest(current: () => boolean, signal?: AbortSignal): Promise<boolean> {
  if (!current()) return Promise.resolve(false);
  if (activeRequests < 2) {
    activeRequests++;
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const waiter: RequestWaiter = {
      current,
      signal,
      resolve,
      active: true,
      onAbort: () => {
        if (!waiter.active) return;
        waiter.active = false;
        removeWaiter(waiter);
        cleanupWaiter(waiter);
        waiter.resolve(false);
      },
    };
    if (signal?.aborted) {
      waiter.onAbort();
      return;
    }
    waitingRequests.push(waiter);
    signal?.addEventListener('abort', waiter.onAbort, { once: true });
  });
}

function releaseRequest(): void {
  while (waitingRequests.length > 0) {
    const waiter = waitingRequests.shift()!;
    if (!waiter.active) continue;
    waiter.active = false;
    cleanupWaiter(waiter);
    if (waiter.signal?.aborted || !waiter.current()) {
      waiter.resolve(false);
      continue;
    }
    waiter.resolve(true);
    return;
  }
  activeRequests--;
}

async function limitedLoad(
  shopId: string,
  load: (shopId: string) => Promise<WbCardLimitsDTO>,
  current: () => boolean,
  signal?: AbortSignal,
): Promise<WbCardLimitsDTO | null> {
  if (!await acquireRequest(current, signal)) return null;
  try { return await load(shopId); }
  finally { releaseRequest(); }
}

/** Read each configured shop's saved ERP result once. This endpoint is read-only;
 * refreshing this view never starts or advances a Wildberries scan.
 */
export async function runShopCardLimitsQueue(
  shops: ShopRequest[],
  load: (shopId: string) => Promise<WbCardLimitsDTO>,
  onResult: (shop: ShopRequest, outcome: Outcome) => void,
  isCurrent: () => boolean = () => true,
  signal?: AbortSignal,
): Promise<void> {
  const current = () => isCurrent() && !signal?.aborted;
  let nextIndex = 0;

  async function readNext(): Promise<void> {
    while (current()) {
      const shop = shops[nextIndex++];
      if (!shop) return;
      try {
        const result = await limitedLoad(shop.id, load, current, signal);
        if (!result || !current()) continue;
        if (result.status === 'refreshing') {
          onResult(shop, { state: 'refreshing', limits: result });
        } else if (result.totalRemaining === null || result.fetchedAt === null) {
          onResult(shop, { state: 'error', message: 'ERP 保存的WB总额度结果不完整，等待下一轮校准。' });
        } else {
          onResult(shop, { state: 'ready', limits: { ...result, status: 'ready', totalRemaining: result.totalRemaining, fetchedAt: result.fetchedAt } });
        }
      } catch (reason) {
        if (current()) onResult(shop, { state: 'error', message: reason instanceof Error ? reason.message : '已保存额度结果读取失败。' });
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(2, shops.length) }, () => readNext()));
}

function resultKey(scopeKey: string, refreshKey: number, shop: ShopRequest): string {
  return `${scopeKey}:${refreshKey}:${shop.id}:${shop.credentialVersion}`;
}

export function useShopCardLimits(shops: ShopDTO[], scopeKey: string, refreshKey: number): Map<string, ShopCardLimitsView> {
  const [results, setResults] = useState<Record<string, Outcome>>({});
  useEffect(() => {
    const readable = shops.filter((shop) => shop.mode === 'real' && Boolean(shop.sellerId));
    if (readable.length === 0) return;
    const controller = new AbortController();
    setResults({});
    void runShopCardLimitsQueue(readable, (shopId) => api.getShopCardLimits(shopId), (shop, outcome) => {
      setResults((current) => ({ ...current, [resultKey(scopeKey, refreshKey, shop)]: outcome }));
    }, undefined, controller.signal);
    return () => { controller.abort(); };
  }, [shops, scopeKey, refreshKey]);

  const views = new Map<string, ShopCardLimitsView>();
  for (const shop of shops) {
    if (shop.mode !== 'real' || !shop.sellerId) {
      views.set(shop.id, { state: 'credentials' });
      continue;
    }
    const result = results[resultKey(scopeKey, refreshKey, shop)];
    views.set(shop.id, result ?? { state: 'loading' });
  }
  return views;
}
